import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChefHat, TriangleAlert, Package, TrendingDown, Zap, X } from "lucide-react";
import api from "@/lib/api";
import AtomCore from "@/components/AtomCore";

/**
 * KitchenAlertsBanner: proactive digest from the Digital Corporate Chef
 * that surfaces the highest-severity issues in the operator's kitchen
 * without waiting for them to open the chat. Reads GET
 * /api/corporate-chef/alerts which returns yield drops, low stock,
 * margin risks, and supplier price spikes ordered by severity.
 *
 * Rendered on the Dashboard so the moment the operator lands, they see
 * "your lamb shoulder yield dropped to 62 percent" or "cheddar is 5
 * points below par". One click opens the Corporate Chef for a deeper
 * conversation about the alert.
 */
const ICONS = {
  yield_drop: TriangleAlert,
  low_stock: Package,
  margin_risk: TrendingDown,
  supplier_price_spike: Zap,
};

const SEV_STYLES = {
  high: {
    bg: "bg-rose-500/10",
    border: "border-rose-500/40",
    text: "text-rose-100",
    icon: "text-rose-300",
    chip: "bg-rose-500/25 text-rose-100",
  },
  medium: {
    bg: "bg-amber-500/10",
    border: "border-amber-500/40",
    text: "text-amber-100",
    icon: "text-amber-200",
    chip: "bg-amber-500/25 text-amber-100",
  },
  info: {
    bg: "bg-white/5",
    border: "border-white/15",
    text: "text-white/85",
    icon: "text-white/70",
    chip: "bg-white/10 text-white/80",
  },
};

export default function KitchenAlertsBanner() {
  const [alerts, setAlerts] = useState([]);
  const [dismissed, setDismissed] = useState(() => {
    try {
      const raw = localStorage.getItem("kitchen_alerts_dismissed");
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/corporate-chef/alerts");
        setAlerts(data?.alerts || []);
      } catch {
        // silently ignore: alerts are additive, never blocking
      } finally {
        setLoaded(true);
      }
    })();
  }, []);

  const persistDismissed = (next) => {
    setDismissed(next);
    try {
      localStorage.setItem("kitchen_alerts_dismissed", JSON.stringify(next));
    } catch {
      // ignore
    }
  };

  const alertKey = (a) => `${a.type}::${a.text.slice(0, 80)}`;
  const visible = alerts.filter((a) => !dismissed.includes(alertKey(a)));

  if (!loaded || visible.length === 0) return null;

  return (
    <div
      className="rounded-2xl glass-card p-4 space-y-3"
      style={{ boxShadow: "0 0 20px #d4af3740" }}
      data-testid="kitchen-alerts-banner"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <AtomCore size={26} testId="kitchen-alerts-atom" />
          <div
            className="label-eyebrow"
            style={{ textShadow: "0 0 10px rgba(212,175,55,0.45)" }}
          >
            Digital Corporate Chef: proactive alerts
          </div>
        </div>
        <Link
          to="/corporate-chef"
          className="text-xs text-amber-200/80 hover:text-amber-100 tabular"
          data-testid="kitchen-alerts-open-chef"
        >
          Ask the Chef about this
        </Link>
      </div>

      <div className="space-y-2">
        {visible.slice(0, 5).map((a, idx) => {
          const sev = SEV_STYLES[a.severity] || SEV_STYLES.info;
          const Icon = ICONS[a.type] || TriangleAlert;
          return (
            <div
              key={alertKey(a)}
              className={`flex items-start gap-3 rounded-xl border ${sev.border} ${sev.bg} px-3 py-2.5`}
              data-testid={`kitchen-alert-${idx}`}
            >
              <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${sev.icon}`} />
              <div className="flex-1">
                <div className={`text-sm leading-relaxed ${sev.text}`}>{a.text}</div>
                <div className="mt-1 flex items-center gap-2">
                  <span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded ${sev.chip}`}>
                    {a.severity}
                  </span>
                  <span className="text-[10px] uppercase tracking-widest text-white/40">
                    {a.type.replaceAll("_", " ")}
                  </span>
                </div>
              </div>
              <button
                type="button"
                onClick={() => persistDismissed([...dismissed, alertKey(a)])}
                className="opacity-60 hover:opacity-100 transition"
                aria-label="Dismiss alert"
                data-testid={`kitchen-alert-dismiss-${idx}`}
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          );
        })}
        {visible.length > 5 && (
          <div className="text-[11px] text-white/50 tabular">
            {visible.length - 5} more open in the Chef console.
          </div>
        )}
      </div>
    </div>
  );
}
